import { type ReactNode } from 'react';
import { Avatar, AvatarImage } from '../../components/ui/avatar';
import { Card } from '../../components/ui/card';
import { type UserDto } from '../../hooks/useGetUsers';
import { cn } from '../../lib/utils';

type UserCardProps = Omit<UserDto, 'id' | 'region'> & {
  onClick?: () => void;
  AvatarOverlay?: ReactNode;
  right?: ReactNode;
  disabled?: boolean;
};

export const UserCard = ({
  internalId,
  displayName,
  pictureUrl,
  jobTitle,
  department,
  units,
  onClick,
  right,
  AvatarOverlay,
  disabled,
}: UserCardProps) => {
  return (
    <Card className="rounded-none border-border p-0 shadow-none">
      <div
        role={onClick ? 'button' : undefined}
        tabIndex={onClick && !disabled ? 0 : undefined}
        onClick={(event) => {
          if (disabled) return;
          if (
            (event.target as HTMLElement).closest(
              'button, input, [role="checkbox"]'
            )
          ) {
            return;
          }
          onClick?.();
        }}
        onKeyDown={(event) => {
          if (!onClick || disabled) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        }}
        aria-disabled={disabled || undefined}
        className={cn(
          'flex items-center gap-2 p-2',
          onClick &&
            !disabled &&
            'cursor-pointer transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <div className="relative size-14 shrink-0">
          <Avatar className="size-14">
            <AvatarImage src={pictureUrl} alt={displayName} />
          </Avatar>
          {AvatarOverlay && (
            <div className="pointer-events-none absolute inset-0">
              {AvatarOverlay}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{displayName}</p>
          <p className="truncate text-xs text-muted-foreground">
            {[internalId, jobTitle, department, units[0]]
              .filter(Boolean)
              .join(', ')}
          </p>
        </div>
        {right}
      </div>
    </Card>
  );
};
